import { UIElement } from '../ui-element';
import {
    fitsHorizontally,
    lowerCaseFirstLetter,
    makePopoverHandler,
    makePopoverMaxHeightHandler,
} from '../../global/helpers';
import { eventBus } from '../../global/event-bus';
import { Labels } from '../../global/labels';
import {
    addOutsideClickHandler,
    insertElements,
    isParentOf,
    rebuildChildren,
} from '../../global/ui-helpers';
import { createElement } from '../../global/render-api';
import { UIBadge } from '../badge/ui-badge';
import { BrandColors } from '../../global/helpers-marketing';
import { TabIndex } from '../../global/keyboard';
import { browser } from '../../global/browser';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type {
    AttributeValue,
    ElementChild,
    ElementChildren,
    ElementConfig,
    ObservedAttributes,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import type {
    UINavToggleLayout,
    UINavToggleLayouts,
    UINavToggleContentLayout,
    UINavToggleContentLayouts,
    UINavToggleEventDetail,
} from './ui-navtoggle.type';
import { UIModal } from '../modal/ui-modal';
import styles from './ui-navtoggle.css';
import type { UIModalWidth, UIModalWidthsLarge } from '../modal/ui-modal.types';

/**
 * @class
 * @memberof SharedComponents
 * @implements {Activatable}
 * @augments {UIElement}
 * @alias UINavToggle
 * @element ui-navtoggle
 * @classdesc Represents a class for <code>ui-navtoggle</code> element
 * @fires event:navtoggle-closed
 * @fires event:menu-toggle
 * @listens event:menu-toggle
 * @property {("default" | "link" | "button")} layout {@attr layout} Layout for toggle:
 *   {@desc default: default layout, toggle with icon}
 *   {@desc link: layout for toggle with icon and text}
 *   {@desc button: layout for toggle with button appearance}
 * @property {("default" | "bounded")} contentLayout {@attr content-layout} Layout for popover content:
 *   {@desc default: default layout}
 *   {@desc bounded: layout without spaces around, but with max-height}
 * @property {UIModalWidthLarge} [popoverWidth="large"] {@attr popover-width} Fixed width of the popover.
 * @property {string} label {@attr label} Label for accessibility.
 * @property {string} for {@attr for} Specify for 'menu-toggle' event details property
 * @property {string} group {@attr group} Specify group of togglers
 * @property {UIIconGlyph} glyph {@attr glyph} Render icon inside component with this glyph
 * (for default layout)
 * @property {boolean} [active=false] {@attr active} State of toggle
 * @property {string} [labelCloseMenu] {@attr label-close-menu} Label for closing menu.
 * @property {string} [labelToggleMenu] {@attr label-toggle-menu} Label for toggling/opening menu.
 * @property {string} [labelClose] {@attr label-close} Label for word 'close',
 * @property {string} badge {@attr badge} Content for badge (for default/link layouts)
 * @property {string} [popoverTitle] {@attr popover-title} The title in the popover header.
 * @property {string} [popoverSubtitle] {@attr popover-subtitle} The subtitle in the popover header.
 * @property {HTMLButtonElement} control Shortcut to actual toggler button
 * @property {HTMLDivElement} popoverElement Shortcut to popover element.
 * @property {HTMLDivElement} content Shortcut to content element.
 * @property {UIBadge | HTMLElement} uiBadge Shortcut to badge element.
 * @property {HTMLElement} footer Shortcut to footer element.
 * @example
 * <ui-navtoggle></ui-navtoggle>
 */
@Component({ tag: 'ui-navtoggle', styles })
export class UINavToggle extends UIElement {
    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-navtoggle', {
            toggleMenu: 'Toggle menu',
            closeMenu: 'Close menu',
            close: 'Close',
        });
    }

    /**
     * Popover width presets in pixels.
     * @returns {{Large: string, XLarge: string}}
     */
    static get PopoverWidth(): UIModalWidthsLarge {
        return {
            Large: 'large',
            XLarge: 'xlarge',
        };
    }

    /**
     * Layouts list.
     * @type {{Default: string, Link: string, Button: string}}
     */
    static readonly Layout: UINavToggleLayouts = {
        Default: 'default',
        Link: 'link',
        Button: 'button',
    };

    /**
     * Content layouts list.
     * @type {{Default: string, Bounded: string}}
     */
    static readonly ContentLayout: UINavToggleContentLayouts = {
        Default: 'default',
        Bounded: 'bounded',
    };

    /**
     * Minimum popover height value in px
     * @type {number}
     */
    static readonly PopoverHeightMin: number = 100;

    /**
     * Bottom gap for popover in px, which takes more height than viewport height
     * @type {number}
     */
    static readonly BottomGap: number = 60;

    @Attribute(String) layout: UINavToggleLayout = UINavToggle.Layout.Default;
    @Attribute(String) label?: string;
    @Attribute(String) for?: string;
    @Attribute(String) group?: string;
    @Attribute(String) glyph?: string;
    @Attribute(Boolean) active: boolean = false;
    @Attribute(String) badge?: string;
    @Attribute(String) contentLayout: UINavToggleContentLayout =
        UINavToggle.ContentLayout.Default;
    @Attribute(String) popoverWidth: string = UINavToggle.PopoverWidth.Large;
    @Attribute(String) labelToggle: TranslationLabels.Label =
        UINavToggle.labels.toggleMenu;
    @Attribute(String) labelCloseMenu: TranslationLabels.Label =
        UINavToggle.labels.closeMenu;
    @Attribute(String) labelClose: TranslationLabels.Label =
        UINavToggle.labels.close;
    @Attribute(String) popoverTitle?: string;
    @Attribute(String) popoverSubtitle?: string;

    @Child('.ui-navtoggle__content') content?: HTMLDivElement;
    @Child('.ui-navtoggle__control') control!: HTMLButtonElement;
    @Child('ui-badge') uiBadge?: UIBadge;
    @Child('.ui-navtoggle__footer') footer?: HTMLDivElement;

    /** @private */
    private _detachedPopover: NullOr<HTMLElement> = null;
    /** @private */
    private outsideClickRemover?: () => void;
    /** @private */
    private resizeHandler?: () => void;
    /** @private */
    private headerResizeObserver?: ResizeObserver;
    /** @private */
    private readonly navigationTopResizeHandler =
        this.updateNavigationTop.bind(this);
    /** Footer reference kept even while it is detached into the modal. */
    /** @private */
    private _footer?: HTMLElement;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'active',
            'badge',
            'label',
            'label-toggle',
            'label-close',
            'label-close-menu',
        ];
    }

    /**
     * @type {HTMLElement | null}
     */
    get popoverElement(): NullOr<HTMLElement> {
        if (this._detachedPopover) {
            return this._detachedPopover;
        }
        return this.querySelector<HTMLElement>('.ui-navtoggle__popover');
    }

    /**
     * @type {HTMLElement | null}
     */
    get modal(): NullOr<UIModal> {
        return this._detachedPopover?.closest<UIModal>('ui-modal') ?? null;
    }

    /**
     * Get popover client height
     * @returns {number}
     */
    getPopoverHeight(): number {
        return this.popoverElement?.clientHeight ?? 0;
    }

    /**
     * Get popover client width
     * @returns {number | undefined}
     */
    getPopoverWidth(): number | undefined {
        const width = UIModal.WindowWidth[this.popoverWidth as UIModalWidth];
        const finalWidth = fitsHorizontally(width) ? width : window.innerWidth;
        this.setPopoverWidth(finalWidth);
        return this.popoverElement?.offsetWidth;
    }

    /**
     * Sets popover width in pixels.
     * @param {number} value
     */
    setPopoverWidth(value: NullOr<number | string>) {
        if (!this.popoverElement) {
            return;
        }
        // Bottom sheet used on mobile devices should have full width,
        // so we don't set any width for it.
        if (browser.mobileMediaMatches()) {
            (this.popoverElement.style.width as string | null) = null;
            return;
        }
        (this.popoverElement.style.width as string | null) =
            (value as number) > 0 ? `${value}px` : null;
    }

    /**
     * Tries to extract the highest heading from the popover content.
     * If it doesn't find one, default close button label is used.
     * @private
     * @param {ElementChildren} children
     * @returns {string}
     */
    private getCloseButtonLabel(
        children: ElementChildren
    ): TranslationLabels.Label | string {
        const headingElements = [].filter
            .call(children, (node: Node) => node.nodeType === Node.ELEMENT_NODE)
            .flatMap((node: HTMLHeadingElement) => {
                const nestedHeadings = [
                    ...node.querySelectorAll<HTMLHeadingElement>('h2'),
                ];
                node.localName === 'h2' && nestedHeadings.push(node);
                return nestedHeadings;
            });

        return headingElements.length > 0
            ? `${UINavToggle.labels.close as string} ${lowerCaseFirstLetter(headingElements[0].textContent ?? '')}`
            : this.labelCloseMenu || UINavToggle.labels.closeMenu;
    }

    /**
     * Calculate possible height for popover on the desktop
     * @param { number } [top]
     * @returns { number }
     */
    static resolvePopoverMaxHeight(top?: number): number {
        const maxHeight =
            document.documentElement.clientHeight -
            (top || 0) -
            UINavToggle.BottomGap;
        return maxHeight > UINavToggle.PopoverHeightMin
            ? maxHeight
            : UINavToggle.PopoverHeightMin;
    }

    /**
     * Open popover in 'modal'
     * @fires event:modal-open
     */
    openDetachedPopover(): void {
        if (this._detachedPopover) {
            return;
        }
        this._detachedPopover = this.popoverElement;

        const popoverHandler = makePopoverHandler(this.control, {
            popoverHeight: this.getPopoverHeight.bind(this),
            popoverWidth: this.getPopoverWidth.bind(this),
            invertHorizontalAlignment: true,
            supportMiddleAlignment: false,
            supportTopAlignment: false,
        });

        this._detachedPopover = this.popoverElement;
        this.dispatchCustomEvent('modal-open', {
            type: 'menu',
            content: this.popoverElement,
            params: {
                classList: {
                    '-navtoggle': true,
                },
                attributes: {
                    'open-timeout': 100,
                    'tablet-full': true,
                    'compact':
                        !browser.mobileMediaMatches() &&
                        !(this.popoverTitle || this.popoverSubtitle)
                            ? true
                            : null,
                    'modal-title': this.popoverTitle,
                    'modal-subtitle': this.popoverSubtitle,
                },
                position: () => {
                    document.documentElement.classList.remove('-no-scroll');
                    return popoverHandler();
                },
                target: this,
                onClose: () => (this.active = false),
            },
            footer: this.resolveFooter() ?? undefined,
        });
    }

    /**
     * Closes detached popover.
     * @fires event:navtoggle-closed
     */
    closeDetachedPopover(): void {
        if (!this._detachedPopover) {
            return;
        }
        const modal = this.modal; // query once
        if (modal) {
            this._detachedPopover = null;
            modal.close?.();
            this.dispatchCustomEvent('navtoggle-closed', {});
        }
    }

    /**
     * Closes popover element.
     */
    closePopover(): void {
        this.active = false;
    }

    /**
     * Resolves the footer element
     * @private
     * @returns {HTMLElement | null}
     */
    private resolveFooter(): NullOr<HTMLElement> {
        return this._footer ?? this.footer ?? null;
    }

    /**
     * Sets or replaces the popover footer content programmatically
     * @param {ElementChildren} children
     */
    setFooter(children: ElementChildren): void {
        let footer = this.resolveFooter();
        if (!footer) {
            footer = createElement<HTMLElement>({
                tagName: 'footer',
                classList: { 'ui-navtoggle__footer': true },
            })!;
            this.appendChild(footer);
        }
        rebuildChildren(footer, children);
        this._footer = footer;
    }

    /**
     * Created popover section with content for navtoggle
     * @param {NodeList | Array<Node>} children
     * @returns {HTMLDivElement}
     */
    createPopoverElement(children: ElementChildren): HTMLDivElement {
        const el = createElement<HTMLDivElement>({
            tagName: 'div',
            attributes: {
                class: 'ui-navtoggle__popover',
                tabindex: TabIndex.Inactive,
            },
            children: [
                {
                    tagName: 'div',
                    classList: {
                        'ui-navtoggle__content': true,
                        '-bounded':
                            this.contentLayout ===
                            UINavToggle.ContentLayout.Bounded,
                    },
                    children:
                        this.contentLayout === UINavToggle.ContentLayout.Bounded
                            ? [
                                  {
                                      tagName: 'div',
                                      classList: {
                                          'ui-navtoggle__content-wrapper': true,
                                      },
                                      children: children,
                                  },
                              ]
                            : children,
                },
            ],
        })!;
        this.appendChild(el);
        return el;
    }

    /**
     * @inheritDoc
     */
    onChangeState(): void {
        this.updateClassList({
            '-on': !!this.active,
            '-off': !this.active,
        });

        if (!this.popoverElement) {
            return;
        }

        if (this.active) {
            this.openDetachedPopover();
            void addOutsideClickHandler(
                this,
                this.handleClickOutside.bind(this) as () => void
            ).then((func) => (this.outsideClickRemover = func));
        } else {
            this.outsideClickRemover?.();
            this.closeDetachedPopover();
        }
    }

    /**
     * Fires callback when user clicked outside of the ui-navtoggle element.
     * @param {Event} e
     * @private
     */
    private handleClickOutside(e: Event): void {
        const targetElement = e.target as HTMLElement;
        if (
            targetElement.closest<UINavToggle>('ui-navtoggle') &&
            e.defaultPrevented
        ) {
            return;
        }

        if (
            this.active &&
            !this.isParentOf(targetElement) &&
            !isParentOf(this.popoverElement!, targetElement)
        ) {
            const modal = targetElement.closest<UIModal>('ui-modal');
            const popoverModal =
                this.popoverElement?.closest<UIModal>('ui-modal');

            if (modal && popoverModal && modal.order > popoverModal.order) {
                return;
            }

            this.closePopover();
        }
    }

    /**
     * Fires callback when element is clicked
     * @param {Event} e
     * @private
     */
    private handleClickEvent(e: Event): void {
        if (e.defaultPrevented || !this.control.contains(e.target as Node)) {
            return;
        }

        e.preventDefault();
        this.dispatchCustomEvent('menu-toggle', {
            active: !this.active,
            for: this.for,
            group: this.group,
        });
    }

    /**
     * Fires callback when the "menu-toggle" event is bubbled
     * @param {CustomEvent} e
     * @private
     */
    private handleToggleMenu(e: CustomEvent<UINavToggleEventDetail>): void {
        if (!e.detail.for && !e.detail.active && !e.detail.group) {
            this.active = false;
            return;
        }

        if (e.detail.group) {
            if (e.detail.group === this.group) {
                this.active = e.detail.for === this.for && !!e.detail.active;
            }
        } else if (e.detail.for === this.for) {
            this.active = !!e.detail.active;
        }
    }

    /**
     * Activate method to open nav-toggle.
     * @fires event:menu-toggle
     */
    activate(): void {
        eventBus.dispatchCustomEvent('menu-toggle', {
            active: true,
            for: this.for,
            group: this.group,
        });
    }

    /**
     * Updates the control accessible name from current label attributes.
     * @private
     */
    private updateControlAriaLabel(): void {
        if (!this.control) {
            return;
        }
        this.control.setAttribute(
            'aria-label',
            (this.label ||
                this.labelToggle ||
                UINavToggle.labels.toggleMenu) as string
        );
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'active':
                this.onChangeState();
                break;
            case 'badge':
                if (!this.control) {
                    return;
                }
                if (this.uiBadge) {
                    if (this.badge) {
                        this.uiBadge.textContent = this.badge;
                    } else {
                        this.uiBadge.remove();
                    }
                } else {
                    const badgeElement = this.createUIBadge();
                    if (badgeElement) {
                        this.control.appendChild(badgeElement);
                    }
                }
                break;
            case 'label':
            case 'label-toggle':
                this.updateControlAriaLabel();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @returns {UIBadge | null}
     * @private
     */
    private createUIBadge(): NullOr<UIBadge> {
        return createElement({
            tagName: 'ui-badge',
            attributes: {
                color: BrandColors.Pineapple,
                layout: UIBadge.Layout.Pin,
            },
            children: [this.badge as ElementChild],
        });
    }

    /**
     * Handles the navigation top position for the main navigation menu,
     * needed for small devices and 200% zoom.
     * @private
     */
    private updateNavigationTop(): void {
        if (this.for !== 'nav-main') {
            return;
        }
        const header = this.closest<HTMLElement>('header.page-header');
        if (!header || header.getClientRects().length === 0) {
            return;
        }
        document.documentElement.style.setProperty(
            '--ui-nav-top',
            `${header.getBoundingClientRect().bottom}px`
        );
    }

    private observeHeaderResize(): void {
        if (this.for !== 'nav-main') {
            return;
        }
        const header = this.closest<HTMLElement>('header.page-header');
        if (!header || typeof ResizeObserver === 'undefined') {
            return;
        }
        this.headerResizeObserver ??= new ResizeObserver(
            this.navigationTopResizeHandler
        );
        this.headerResizeObserver.observe(header);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const childNodes = this.detachChildNodes();
        const iconNode: UndefinedOr<HTMLElement> = childNodes.find((node) => {
            return ['ui-icon', 'ui-icon-symbolic'].includes(node.localName);
        });
        const content: UndefinedOr<HTMLElement> = childNodes.find((node) => {
            return node.localName === 'section';
        });
        const footer: UndefinedOr<HTMLElement> = childNodes.find((node) => {
            return node.localName === 'footer';
        });
        if (content && childNodes.includes(content)) {
            childNodes.splice(childNodes.indexOf(content), 1);
        }
        if (footer && childNodes.includes(footer)) {
            childNodes.splice(childNodes.indexOf(footer), 1);
        }
        const buttonNode: UndefinedOr<HTMLElement> = childNodes.find(
            (node) => node.localName === 'button'
        );

        const control = this.createElement({
            tagName: 'button',
            attributes: {
                'type': 'button',
                'aria-haspopup': 'true',
                'aria-label':
                    this.label ||
                    this.labelToggle ||
                    UINavToggle.labels.toggleMenu,
            },
            classList: {
                'ui-navtoggle__control': true,
            },
            element: buttonNode!,
        });

        if (
            (
                [
                    UINavToggle.Layout.Default,
                    UINavToggle.Layout.Link,
                ] as Array<UINavToggleLayout>
            ).includes(this.layout)
        ) {
            insertElements(control, [
                {
                    tagName: 'ins',
                    children: [
                        { tagName: 'span' },
                        { tagName: 'span' },
                        { tagName: 'span' },
                        { tagName: 'span' },
                    ],
                },
            ]);

            insertElements(
                control,
                this.layout === UINavToggle.Layout.Link
                    ? childNodes
                    : ([
                          this.glyph || iconNode
                              ? {
                                    tagName: 'ui-icon',
                                    attributes: {
                                        glyph:
                                            this.glyph ||
                                            iconNode?.getAttribute('glyph'),
                                    },
                                    element: iconNode,
                                }
                              : null,
                      ] as Array<ElementConfig>)
            ); /** TODO: Handle null case properly */
        }

        // Insert badge as last element inside a control, to do not break the flow
        // and avoid z-index usage.
        this.badge && insertElements(control, [this.createUIBadge()!]);

        this.updateElement({
            classList: {
                '-on': !!this.active,
                '-off': !this.active,
                '-iconed': !!(this.glyph || iconNode),
                '-symbolic': !!control.querySelector('ui-icon-symbolic'),
            },
            children: [control],
        });

        if (content) {
            this.createPopoverElement(content.childNodes);
        }
        if (footer) {
            footer.classList.add('ui-navtoggle__footer');
            this.appendChild(footer);
            this._footer = footer;
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.handleClickEvent = this.handleClickEvent.bind(this);
        this.handleToggleMenu = this.handleToggleMenu.bind(this);

        eventBus.addEventListener(
            'menu-toggle',
            this.handleToggleMenu as () => void
        );
        this.addEventListener('click', this.handleClickEvent);
        this.onChangeState();
        if (this.popoverElement) {
            this.resizeHandler = makePopoverMaxHeightHandler(
                this.popoverElement,
                0
            );
            this.resizeHandler();
        }
        this.updateNavigationTop();
        this.observeHeaderResize();
        window.addEventListener('resize', this.navigationTopResizeHandler);
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.closeDetachedPopover();
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        this.headerResizeObserver?.disconnect();
        window.removeEventListener('resize', this.navigationTopResizeHandler);
        this.outsideClickRemover?.();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.updateNavigationTop();
        this.observeHeaderResize();
        window.addEventListener('resize', this.navigationTopResizeHandler);
    }
}
